// Notification Tools
// Send notifications via notification_worker (email, telegram, phone)
// Each notification type is a separate tool for better LLM tool-calling accuracy.

import { validateRequired } from '../../utils/validation.js'
import { forwardCallByName } from 'workers-common'

// --- Raw implementations (for worker sandbox RPC) ---
export function createNotificationTools({ getWorkerClient }) {
  const sendEmail = async (ctx, args) => {
    validateRequired(args, ['toEmailAddress', 'subject', 'msg'], 'sendEmail')
    const workerClient = getWorkerClient()
    const result = await forwardCallByName(ctx, workerClient, 'notification-worker', 'notifyByEmail', [{
      toEmailAddress: args.toEmailAddress,
      toName: args.toName || '',
      subject: args.subject,
      msg: args.msg,
      priority: args.priority || 'normal',
    }])
    return { success: true, type: 'email', result }
  }

  const sendTelegram = async (ctx, args) => {
    validateRequired(args, ['botId', 'chatId', 'text'], 'sendTelegram')
    const workerClient = getWorkerClient()
    const result = await forwardCallByName(ctx, workerClient, 'notification-worker', 'notifyByTgbot', [{
      botId: args.botId,
      chatId: args.chatId,
      text: args.text,
      parseMode: args.parseMode || 'Markdown',
    }])
    return { success: true, type: 'telegram', result }
  }

  const sendPhone = async (ctx, args) => {
    validateRequired(args, ['toPhoneNumber', 'message'], 'sendPhone')
    const workerClient = getWorkerClient()
    const result = await forwardCallByName(ctx, workerClient, 'notification-worker', 'notifyByPhoneCall', [{
      toPhoneNumber: args.toPhoneNumber,
      message: args.message,
      language: args.language || 'en-US',
      voice: args.voice || 'male',
      maxRetries: args.maxRetries || 1,
    }])
    return { success: true, type: 'phone', result }
  }

  return { sendEmail, sendTelegram, sendPhone }
}

// --- ToolEntries (for ToolRegistry) ---
export function createNotificationToolEntries({ getWorkerClient, workerName = 'myagent-worker' }) {
  return [
    {
      id: 'communication/sendEmail',
      name: 'sendEmail',
      source: 'builtin',
      description: 'Send an email notification. 通过邮件发送通知。',
      parameters: {
        type: 'object',
        properties: {
          toEmailAddress: { type: 'string', description: 'Recipient email address' },
          subject: { type: 'string', description: 'Email subject' },
          msg: { type: 'string', description: 'Email body content' },
        },
        required: ['toEmailAddress', 'subject', 'msg'],
      },
      searchText: 'sendEmail\nSend email notification message mail notify alert\n发邮件 写邮件 发一封邮件 测试邮件 电子邮件 邮箱 邮件通知 发送通知 通知',
      examples: [
        { input: 'send an email to user@example.com about the meeting', args: { toEmailAddress: 'user@example.com', subject: 'Meeting', msg: 'Meeting reminder' } },
        { input: '帮我发一封邮件通知他明天开会', args: { toEmailAddress: 'colleague@company.com', subject: '会议通知', msg: '明天上午10点开会' } },
      ],
      execute: async (ctx, args) => {
        const workerClient = getWorkerClient()
        return forwardCallByName(ctx, workerClient, workerName, 'notification.sendEmail', [args])
      },
    },
    {
      id: 'communication/sendTelegram',
      name: 'sendTelegram',
      source: 'builtin',
      description: 'Send a notification via Telegram. 通过Telegram发送通知。',
      parameters: {
        type: 'object',
        properties: {
          botId: { type: 'string', description: 'Telegram bot ID' },
          chatId: { type: 'string', description: 'Telegram chat ID' },
          text: { type: 'string', description: 'Message text' },
        },
        required: ['botId', 'chatId', 'text'],
      },
      searchText: 'sendTelegram\nSend telegram notification message TG bot notify alert\n发电报 Telegram消息 TG消息 发消息 通知 提醒',
      examples: [
        { input: 'send a telegram message to the group', args: { botId: 'bot123', chatId: 'chat456', text: 'Hello' } },
        { input: '用Telegram发个消息提醒一下', args: { botId: 'bot123', chatId: 'chat456', text: '提醒' } },
      ],
      execute: async (ctx, args) => {
        const workerClient = getWorkerClient()
        return forwardCallByName(ctx, workerClient, workerName, 'notification.sendTelegram', [args])
      },
    },
    {
      id: 'communication/sendPhone',
      name: 'sendPhone',
      source: 'builtin',
      description: 'Send a notification via phone call. 通过电话发送通知。',
      parameters: {
        type: 'object',
        properties: {
          toPhoneNumber: { type: 'string', description: 'Phone number to call' },
          message: { type: 'string', description: 'Message to speak' },
        },
        required: ['toPhoneNumber', 'message'],
      },
      searchText: 'sendPhone\nPhone call notification notify alert ring\n打电话 电话通知 来电提醒 通知',
      examples: [
        { input: 'call +1234567890 and tell them the server is down', args: { toPhoneNumber: '+1234567890', message: 'Server is down' } },
        { input: '打电话通知他服务器挂了', args: { toPhoneNumber: '+8612345678900', message: '服务器挂了' } },
      ],
      execute: async (ctx, args) => {
        const workerClient = getWorkerClient()
        return forwardCallByName(ctx, workerClient, workerName, 'notification.sendPhone', [args])
      },
    },
  ]
}
